import type { AppData } from '../../types/app_data';
import type { Step } from '../types/step';
import { StepCategory } from '../../types/app_data';

import { STACK_DEPTH_QUERY_PARAM, stackStepQueryParams } from './step_stack';

export const excludeFromProgressCalculation: Step['excludeFromProgressCalculation'] = (appData) => {
  return !!appData.addingNewCar;
};

/**
 * Get list of adding car steps.
 */
export function getAddCarSteps(options: {
  /**
   * Callback to determine in which flow should the steps show.
   * This will be used in `Step.canShow`, along with each step's own rules.
   */
  flowFilter: Step['canShow'];
}): Step[] {
  const canShow: Step['canShow'] = (...args) => {
    const [appData] = args;
    if (appData.isOnboardingLite) {
      return false;
    }
    if (appData.signupParams?.needNNOInsurance === true) {
      return false;
    }
    return options.flowFilter(...args);
  };

  return [
    {
      id: 'addCarMake',
      pathname: '/addcarmake',
      queryParams: stackStepQueryParams,
      excludeFromProgressCalculation,

      canShow: (appData) => {
        if (appData.isFromMAL) return false;

        if (appData.initialCarAddCarByVin) {
          return false;
        }

        return canShow(appData);
      },
      hasData: () => true,
      hasOwnData: (appData: AppData) =>
        !!(
          appData.initialCarAddCarByVin ||
          (appData.addingNewCar ? appData.addingNewCar.car.make : appData.signupParams?.initialCar?.make)
        ),

      category: () => StepCategory.Cars,
    },
    {
      id: 'addCarModel',
      pathname: '/addcarmodel',
      queryParams: stackStepQueryParams,
      excludeFromProgressCalculation,

      canShow: (appData) => {
        if (appData.isFromMAL) return false;

        if (appData.initialCarAddCarByVin) {
          return false;
        }

        return canShow(appData);
      },
      hasData: () => true,
      hasOwnData: (appData: AppData) =>
        !!(
          appData.initialCarAddCarByVin ||
          (appData.addingNewCar ? appData.addingNewCar.car.model : appData.signupParams?.initialCar?.model)
        ),
      category: () => StepCategory.Cars,
    },
    {
      id: 'addCarSubmodel',
      pathname: '/addcarsubmodel',
      queryParams: stackStepQueryParams,
      excludeFromProgressCalculation,

      canShow: (appData) => {
        if (appData.isFromMAL) return false;

        if (appData.initialCarAddCarByVin) {
          return false;
        }

        if (
          appData.signupParams?.initialCar?.additionalInfo?.isInitialCarDefaultTrim ||
          appData.addingNewCar?.car.additionalInfo?.isInitialCarDefaultTrim
        ) {
          return false;
        }

        return canShow(appData);
      },
      hasData: () => true,
      hasOwnData: (appData: AppData) =>
        !!(
          appData.initialCarAddCarByVin ||
          (appData.addingNewCar ? appData.addingNewCar.car.partialVin : appData.signupParams?.initialCar?.partialVin)
        ),
      category: () => StepCategory.Cars,
    },
    {
      id: 'addCarVin',
      pathname: '/addcarvin',
      queryParams: stackStepQueryParams,
      excludeFromProgressCalculation,

      canShow: (appData) => {
        return canShow(appData) && !!appData.initialCarAddCarByVin;
      },
      hasData: () => true,
      hasOwnData: (appData: AppData) =>
        appData.addingNewCar ? !!appData.addingNewCar.car.vin : !!appData.signupParams?.initialCar?.vin,
      category: () => StepCategory.Cars,
    },
    {
      id: 'addCarMileage',
      pathname: '/addcarmileage',
      queryParams: stackStepQueryParams,
      excludeFromProgressCalculation,

      canShow: (appData) => {
        return canShow(appData);
      },
      hasData: () => true,
      hasOwnData: (appData: AppData) => !!appData.addedInitialCarMileage || true,
      category: () => StepCategory.Cars,
    },
  ];
}

/**
 * TODO: Review this after signup_rm_adding_modal test is finished.
 *
 * The `specialNextStep` for the `/cars` step.
 */
export const carsSpecialNextStep: Step['specialNextStep'] = {
  enable: (appData) => {
    return !!appData.addingNewCar;
  },
  nextStepId: 'addCarYear',
};

/**
 * TODO: Review this after signup_rm_adding_modal test is finished.
 */
export const addCarYearQueryParams: Step['queryParams'] = (appData, router) => {
  if (appData?.addingNewCar) {
    return {
      [STACK_DEPTH_QUERY_PARAM]: '1',
    };
  }
  return {};
};
